import { Moon } from 'lucide-react';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Cell,
} from 'recharts';

const fmtH = (h) => `${Math.floor(h)}h ${String(Math.round((h % 1) * 60)).padStart(2, '0')}m`;

export const SleepSection = ({ days }) => {
  const withSleep = days.filter((d) => {
    const m = d.metrics || {};
    return m.sleep_hours != null || m.sleep_feel != null;
  });
  const last = withSleep[withSleep.length - 1];
  const m = last?.metrics || {};
  const hasDuration = days.some((d) => (d.metrics || {}).sleep_hours != null);
  const chart = days.slice(-14).map((d) => ({
    date: d.date,
    hours: (d.metrics || {}).sleep_hours ?? null,
    feel: (d.metrics || {}).sleep_feel ?? null,
  }));

  return (
    <section data-testid="nexa-sleep-section">
      <h2 className="text-[11px] tracking-[0.25em] uppercase text-sky-400/70 font-mono mb-3">Sleep</h2>
      <div className="flex flex-col gap-4">
        <div className="rounded-3xl border border-white/10 bg-[#0A0A0A] p-6" data-testid="nexa-sleep-card">
          <div className="flex items-center gap-2 mb-4">
            <Moon className="w-4 h-4 text-sky-400" strokeWidth={1.5} />
            <span className="text-xs tracking-[0.2em] uppercase text-white/40">
              Last night{last ? ` · ${new Date(last.date + 'T00:00:00').toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}` : ''}
            </span>
          </div>
          {last ? (
            <>
              <div className="flex items-end gap-6 flex-wrap">
                <div>
                  <div className="nexa-heading text-5xl font-light" data-testid="nexa-sleep-hours">
                    {m.sleep_hours != null ? fmtH(Number(m.sleep_hours)) : '—'}
                  </div>
                  <p className="text-xs text-white/40 mt-1">duration</p>
                </div>
                {m.sleep_feel != null && (
                  <div>
                    <div className="nexa-heading text-3xl font-light text-sky-400" data-testid="nexa-sleep-feel">
                      {m.sleep_feel}<span className="text-base text-white/40">/10</span>
                    </div>
                    <p className="text-xs text-white/40 mt-1">how it felt</p>
                  </div>
                )}
                {m.sleep_efficiency_pct != null && (
                  <div>
                    <div className="nexa-heading text-3xl font-light">{m.sleep_efficiency_pct}%</div>
                    <p className="text-xs text-white/40 mt-1">efficiency</p>
                  </div>
                )}
              </div>
              <div className="flex gap-4 flex-wrap mt-4 text-[11px] font-mono text-white/45">
                {m.sleep_start && m.sleep_end && <span>{m.sleep_start} → {m.sleep_end}</span>}
                {m.in_bed_minutes != null && <span>in bed {fmtH(m.in_bed_minutes / 60)}</span>}
                {m.deep_minutes != null && <span>deep {Math.round(m.deep_minutes)}m</span>}
                {m.rem_minutes != null && <span>REM {Math.round(m.rem_minutes)}m</span>}
                {m.core_minutes != null && <span>core {Math.round(m.core_minutes)}m</span>}
                {m.awake_minutes != null && <span>awake {Math.round(m.awake_minutes)}m</span>}
                {m.awakenings != null && <span>{m.awakenings} wake-ups</span>}
                {m.resting_hr != null && <span>resting HR {m.resting_hr}</span>}
              </div>
              {m.sleep_note && (
                <p className="text-[13px] text-white/55 mt-3 leading-relaxed">“{m.sleep_note}”</p>
              )}
            </>
          ) : (
            <p className="text-sm text-white/40">No sleep data yet.</p>
          )}
          {!hasDuration && (
            <div className="mt-4 rounded-xl border border-amber-400/25 bg-amber-400/[0.05] px-4 py-3"
              data-testid="nexa-sleep-sync-hint">
              <p className="text-[12px] text-amber-200/80 leading-relaxed">
                Your iPhone Shortcut currently sends only steps. Add a Sleep step to it that outputs
                stage lines (<span className="font-mono text-amber-200">Value|Start|End</span>) into
                a <span className="font-mono">sleep_raw</span> text field — full sleep statistics
                will appear here automatically.
              </p>
            </div>
          )}
        </div>

        <div className="rounded-3xl border border-white/10 bg-[#0A0A0A] p-6" data-testid="nexa-sleep-chart">
          <span className="text-xs tracking-[0.2em] uppercase text-white/40">
            {hasDuration ? 'Sleep duration · 14 days' : 'Sleep feel · 14 days'}
          </span>
          {chart.some((c) => (hasDuration ? c.hours != null : c.feel != null)) ? (
            <div className="h-40 mt-4 -ml-4">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chart}>
                  <XAxis dataKey="date" tick={{ fill: 'rgba(255,255,255,0.35)', fontSize: 10 }}
                    tickFormatter={(d) => d.slice(8)} axisLine={false} tickLine={false} />
                  <YAxis domain={[0, hasDuration ? 10 : 10]}
                    tick={{ fill: 'rgba(255,255,255,0.35)', fontSize: 10 }}
                    axisLine={false} tickLine={false} width={30} />
                  <Tooltip contentStyle={{ background: '#0A0A0A', border: '1px solid rgba(255,255,255,0.1)',
                    borderRadius: 12, color: '#fff', fontSize: 12 }}
                    formatter={(v) => [hasDuration ? fmtH(Number(v)) : `${v}/10`, hasDuration ? 'Sleep' : 'Feel']} />
                  <Bar dataKey={hasDuration ? 'hours' : 'feel'} radius={[6, 6, 0, 0]}>
                    {chart.map((c) => (
                      <Cell key={c.date}
                        fill={(hasDuration ? c.hours >= 7 : c.feel >= 7) ? '#38BDF8' : 'rgba(56,189,248,0.35)'} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <p className="text-sm text-white/40 mt-4">
              Answer the morning check-in for a few days to build this chart.
            </p>
          )}
        </div>
      </div>
    </section>
  );
};
